<template>
  <div
    ref="gallery"
    class="relative"
    :class="{ 'flex-1': isFocused }"
    data-test-id="product-card-gallery-wrapper"
    @mouseenter="activateDesktopCarousel"
  >
    <div
      v-style:aspect-ratio="aspectRatio"
      class="relative w-full"
      :class="{ 'h-full': isFocused }"
    >
      <template v-if="mediaVisible">
        <lazy-product-card-carousel
          v-if="showCarousel"
          :asset-override
          class="absolute-0"
          :class="{ 'h-full': isFocused }"
          :focused
          :items
          :lazy
          :name
        >
          <template #link>
            <slot name="link" />
          </template>
        </lazy-product-card-carousel>
        <template v-else>
          <base-picture
            :alt="assetOverride ? name : items[0]?.alt || `${name} - 1`"
            class="w-full"
            data-test-id="product-card-pic"
            :height="mediaHeight"
            :lazy
            :props-img="{ class: ['w-a', { 'absolute-0 full cover': isFocused }], fetchpriority }"
            :src="primarySrc"
            :width="mediaWidth"
          />
          <transition v-if="secondaryItem?.src" name="fade">
            <component
              :is="secondaryItem.type === 'image' ? BasePicture : BaseVideo"
              v-if="!disableGallery && showSecondaryMedia"
              :alt="secondaryItem.alt || `${name} - 2`"
              autoplay
              class="absolute-0 full"
              :class="{ 'bg-black': secondaryItem.type !== 'image' }"
              data-test-id="product-card-pic-hover"
              :fit="isFocused ? 'cover' : 'contain'"
              :height="mediaHeight"
              :lazy
              loop
              :src="secondaryItem.type === 'image'
                ? getResponsiveImageTransformations(secondaryItem.src, secondaryTransformations)
                : getVideoTransformations(secondaryItem.src, $feature.configVideoPresets || {})"
              :width="mediaWidth"
            />
          </transition>
        </template>
      </template>
      <slot v-if="!showCarousel || !mediaVisible" name="link" />
    </div>
    <slot name="badge" />
  </div>
</template>

<script lang="ts" setup>
import type { ProductGalleryExtended } from '#root/api/clients/product/data-contracts'
import type { Responsive } from '#types/common'
import type { ImgHTMLAttributes } from '#types/components/base/picture'
import { BasePicture, BaseVideo } from '#components'

export type Props = {
  assetOverride?: Responsive<string>
  focused?: Responsive<boolean>
  items?: ProductGalleryExtended
  lazy?: boolean
  name: string
  productId?: string
  showSecondaryMedia?: boolean
}

const { assetOverride, focused, items = [], name, showSecondaryMedia } = defineProps<Props>()

const { $feature, $viewport } = useNuxtApp()
const { card: { mediaTransformations }, defaultImageUrl } = useAppConfig().components.product
const { fetchpriority } = useAttrs() as ImgHTMLAttributes
const {
  aspectRatio,
  isFocused,
  width: mediaWidth,
  height: mediaHeight,
  transformations: focusedTransformations
} = useProductCardMedia(() => focused)

const gallery = useTemplateRef('gallery')
// Render media in the initial SSR response; the observer reclaims it after hydration offscreen.
const mediaVisible = ref(true)
const desktopCarouselRequested = ref(false)
const disableGallery = inject<boolean>('disableGallery', false)

const primaryTransformations = computed(() => ({
  height: mediaHeight.value,
  width: mediaWidth.value,
  assetTransformation: items[0]?.type === 'image' ? items[0].assetTransformation : undefined,
  ...mediaTransformations,
  ...focusedTransformations.value,
}))

const primarySrc = computed(() => assetOverride
  ? getAssetOverrideSrc(assetOverride, primaryTransformations.value)
  : getResponsiveImageTransformations(items[0]?.src, primaryTransformations.value) || defaultImageUrl
)

const secondaryItem = computed(() => items[assetOverride ? 0 : 1])
const secondaryTransformations = computed(() => ({
  height: mediaHeight.value,
  width: mediaWidth.value,
  assetTransformation: secondaryItem.value?.type === 'image' ? secondaryItem.value.assetTransformation : undefined,
  ...mediaTransformations,
  ...focusedTransformations.value,
}))
const showCarousel = computed(() =>
  $feature.enableProductCardGalleryCarousel
  && !disableGallery
  && Boolean(secondaryItem.value?.src)
  && (showSecondaryMedia || desktopCarouselRequested.value)
)

const activateDesktopCarousel = () => {
  if (!$viewport.lg) return

  mediaVisible.value = true
  desktopCarouselRequested.value = true
}

if ($feature.enableProductCardGalleryCarousel) {
  useIntersectionObserver(gallery, ([entry]) => {
    mediaVisible.value = entry?.isIntersecting ?? true
  }, { rootMargin: '300px 0px' })
}

// Preserve hover-image preloading only when the carousel memory bound is inactive.
onMounted(() => {
  if ($feature.enableProductCardGalleryCarousel || disableGallery || !showSecondaryMedia) return

  const item = secondaryItem.value
  if (item?.src && item.type === 'image') {
    const preload = () => {
      const transformedSrc = getResponsiveImageTransformations(item.src, secondaryTransformations.value)

      if (isString(transformedSrc)) {
        new Image().src = transformedSrc
      }
      else if (transformedSrc) {
        Object.values(transformedSrc).forEach((src) => {
          new Image().src = src
        })
      }
    }

    if ('requestIdleCallback' in window) {
      requestIdleCallback(preload)
    }
    else {
      setTimeout(preload, 200)
    }
  }
})
</script>
